import { useRef } from 'react';
import { Button, OutlinedSelect, TextOutlinedInput } from '@octano/global-ui';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../locales/translations';
import ClassroomFormModal, {
  ClassroomFormModalMethods,
} from './modal/ClassroomFormModal';
import {
  ITEMS_PER_PAGE_DEFAULT,
  PAGE_DEFAULT,
  useClassrooms,
} from './Loaders/ClassroomsLoader';
import { useFormData } from './SearchControlsLoader';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { SearchControlsForm } from '../types';

const DEFAULT_VALUES = {
  name: null,
  code: null,
  type: null,
  campus: null,
  state: null,
};

export default function SearchControls() {
  const { t } = useTranslation(TranslationsKeys.MANAGEMENT_TOOLS_CLASSROOMS);
  const classroomModalRef = useRef<ClassroomFormModalMethods>(null);
  const { refresh } = useClassrooms();

  const reloadClassroomList = () => {
    refresh();
  };

  const handleAddClassroom = () => {
    classroomModalRef.current?.open();
  };
  const { data } = useFormData();
  const { updateQuery, loading } = useClassrooms();
  const { control, handleSubmit, reset } = useForm<SearchControlsForm>({
    defaultValues: DEFAULT_VALUES,
  });

  const codeOptions = useMemo(() => {
    return data?.classroomCodes.map((code) => ({
      value: code.code,
      label: code.code,
    }));
  }, [data]);

  const typeOptions = useMemo(() => {
    return data?.classroomTypes.map((type) => ({
      value: type.id,
      label: type.name,
    }));
  }, [data]);

  const campusOptions = useMemo(() => {
    return data?.campuses.map((campus) => ({
      value: campus.id,
      label: campus.name,
    }));
  }, [data]);

  const onSubmit = useCallback(
    (data: SearchControlsForm) => {
      updateQuery({
        items: ITEMS_PER_PAGE_DEFAULT,
        page: PAGE_DEFAULT,
        name: data.name?.trim() as string,
        code: data.code?.value as string,
        type: data.type?.value as number,
        campus: data.campus?.value as number,
        state: data.state ? data.state.value.toString() : undefined,
      });
    },
    [updateQuery],
  );

  const handleClear = useCallback(() => {
    reset();
    updateQuery({
      items: ITEMS_PER_PAGE_DEFAULT,
      page: PAGE_DEFAULT,
    });
  }, [reset, updateQuery]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Row xs={12}>
        <Col xs={12} lg={6} xl={3}>
          <TextOutlinedInput
            name="name"
            label={t('filters.name.label')}
            placeholder={t('filters.name.placeholder')}
            control={control}
            disabled={loading}
          />
        </Col>
        <Col xs={12} lg={6} xl={3}>
          <OutlinedSelect
            name="code"
            label={t('filters.code.label')}
            placeholder={t('filters.code.placeholder')}
            options={codeOptions || []}
            control={control}
            disabled={loading}
          />
        </Col>
        <Col xs={12} lg={6} xl={3}>
          <OutlinedSelect
            name="type"
            label={t('filters.type.label')}
            placeholder={t('filters.type.placeholder')}
            options={typeOptions || []}
            control={control}
            disabled={loading}
          />
        </Col>
        <Col xs={12} lg={6} xl={3}>
          <OutlinedSelect
            name="campus"
            label={t('filters.campus.label')}
            placeholder={t('filters.campus.placeholder')}
            options={campusOptions || []}
            control={control}
            disabled={loading}
          />
        </Col>
      </Row>
      <Row xs={12} className="d-flex justify-content-between">
        <Col xs={12} lg={6} xl={3}>
          <OutlinedSelect
            name="state"
            label={t('filters.state.label')}
            placeholder={t('filters.state.placeholder')}
            options={[
              { value: 'true', label: 'Habilitado' },
              { value: 'false', label: 'Deshabilitado' },
            ]}
            control={control}
            disabled={loading}
          />
        </Col>
        <Col xs={12} lg={6} xl={3}>
          <Row xs={12} className="d-flex align-items-center h-100 mt-1">
            <Col xs={6}>
              <Button
                text={t('filters.clean')}
                size="md"
                outlined
                fullwidth
                onClick={handleClear}
                disabled={loading}
              />
            </Col>
            <Col xs={6}>
              <Button
                text={t('filters.search')}
                type="submit"
                size="md"
                fullwidth
                disabled={loading}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row xs={12} className="d-flex justify-content-end mt-2 mb-5">
        <Col xs={12} lg={6} xl={3}>
          <Button
            text={t('filters.addClassroom')}
            size="md"
            outlined
            fullwidth
            onClick={handleAddClassroom}
          />
        </Col>
      </Row>

      <ClassroomFormModal
        ref={classroomModalRef}
        onSave={reloadClassroomList}
      />
    </form>
  );
}
